import {
  ClearAllOutlined,
  DeleteOutline,
  PersonAddAlt1Outlined,
  SettingsOutlined,
} from "@mui/icons-material";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { Can, usePermission } from "src/helpers/Auth";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { ClearDatasetModal } from "src/views/forms/DatasetModals";
import { DeleteDatasetModal } from "src/views/forms/datasetModels/DeleteDatasetModal";
import { UpdateDatasetModal } from "src/views/forms/datasetModels/UpdateDatasetModal";
import ShareDialog from "src/reusable/components/ShareDialog";

type DatasetCardActionMenuProps = {
  isOpen: boolean;
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
  dataset: any;
};

const DatasetCardActionMenu = ({
  isOpen,
  anchorEl,
  setAnchorEl,
  dataset,
}: DatasetCardActionMenuProps) => {
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openClearModal, setOpenClearModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);

  // An owner can always manage their dataset's sharing, regardless of the
  // dataset:update RBAC role (which is a global capability, not per-object).
  // The backend enforces per-object "manage" either way.
  const currentUser = useGetUserDetails();
  const isOwner = !!currentUser && dataset?.create_user_fk === currentUser.id;
  // Ownership transfer is owner-or-admin (the backend enforces the same).
  const { canIt: isAdmin } = usePermission("admin");

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateDataset = () => {
    setOpenUpdateModal(true);
    handleClose();
  };

  const handleClearDataset = () => {
    setOpenClearModal(true);
    handleClose();
  };

  const handleDeleteDataset = () => {
    setOpenDeleteModal(true);
    handleClose();
  };

  const handleShareDataset = () => {
    setOpenShareModal(true);
    handleClose();
  };

  return (
    <>
      <Menu
        id="dataset-action-menu"
        aria-labelledby="more-dataset-actions"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Can
          perform="dataset:update"
          yes={() => (
            <MenuItem
              onClick={handleUpdateDataset}
              id={`update-dataset-button-${dataset.name}`}
              data-testid={`ds-btn-edit-${dataset.id}`}
              data-attr={`datasets:menu:edit-dataset-${dataset.id}`}
              hidden={dataset?.remote_dataset}
            >
              <ListItemIcon>
                <SettingsOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
        />
        {(() => {
          const shareItem = (
            <MenuItem
              onClick={handleShareDataset}
              id={`share-dataset-button-${dataset.name}`}
              data-testid={`ds-btn-share-${dataset.id}`}
              data-attr={`datasets:menu:share-dataset-${dataset.id}`}
            >
              <ListItemIcon>
                <PersonAddAlt1Outlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage access</ListItemText>
            </MenuItem>
          );
          // Owners see it directly; otherwise it's gated on the RBAC capability.
          return isOwner ? (
            shareItem
          ) : (
            <Can perform="dataset:update" yes={() => shareItem} />
          );
        })()}
        <Can
          perform="dataset:update"
          yes={() => (
            <MenuItem
              onClick={handleClearDataset}
              id={`clear-dataset-button-${dataset.name}`}
              data-testid={`ds-btn-clear-${dataset.id}`}
              data-attr={`datasets:menu:clear-dataset-${dataset.id}`}
            >
              <ListItemIcon>
                <ClearAllOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Clear</ListItemText>
            </MenuItem>
          )}
        />
        <Can
          perform="dataset:delete"
          yes={() => (
            <MenuItem
              onClick={handleDeleteDataset}
              id={`delete-dataset-button-${dataset.name}`}
              data-testid={`ds-btn-delete-${dataset.id}`}
              data-attr={`datasets:menu:delete-dataset-${dataset.id}`}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )}
        />
      </Menu>
      {openUpdateModal && (
        <UpdateDatasetModal
          id={dataset.id}
          datasetName={dataset.name}
          datasetDescription={dataset.description}
          datasetTags={dataset.tags}
          isVisible={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
        />
      )}
      {openClearModal && (
        <ClearDatasetModal
          id={dataset.id}
          isVisible={openClearModal}
          datasetName={dataset.name}
          onClose={() => setOpenClearModal(false)}
        />
      )}
      {openDeleteModal && (
        <DeleteDatasetModal
          id={dataset.id}
          isVisible={openDeleteModal}
          datasetName={dataset.name}
          onClose={() => setOpenDeleteModal(false)}
        />
      )}
      {openShareModal && (
        <ShareDialog
          open={openShareModal}
          resource={{ type: "dataset", id: dataset.id, name: dataset.name }}
          canTransferOwnership={isOwner || isAdmin}
          onClose={() => setOpenShareModal(false)}
        />
      )}
    </>
  );
};

export default DatasetCardActionMenu;
